// ENG-6949: recover content from FORWARDED / SHARED Slack messages.
//
// When a user shares/forwards a message into an agent's channel, Slack delivers
// it as a `message` event whose top-level `text`/`files` carry only the user's
// own wrapper note (often empty) — the forwarded message's actual text, files
// and any link/image unfurls live inside the `attachments[]` array, which the
// pipeline never read. The agent therefore saw "your message, no attachment or
// quoted content".
//
// Each share/unfurl attachment looks roughly like:
//   { text, fallback, author_name, ts,
//     files: [ <file objects> ],                   // forwarded uploaded files
//     message_blocks: [ { message: { blocks } } ], // rich-text version
//     title, title_link, from_url,                 // link unfurl
//     image_url, thumb_url,                         // embedded/preview image
//     is_share: true }
//
// Two classes of embedded media, handled differently on purpose:
//   1. Uploaded Slack FILES (att.files) — returned as file objects and routed
//      through the authenticated files.info download path (caller). Safe: every
//      fetch is to Slack with the bot token.
//   2. Link/image UNFURLS (image_url / title_link / from_url) — plain URLs, often
//      external and attacker-influenceable (anyone who can forward to the agent
//      controls them). We SURFACE these as text so the agent can see them and
//      fetch via its own sandboxed, policy-gated web tools. We deliberately do
//      NOT GET them here: a blind fetch in the privileged ingestion host would be
//      an SSRF sink (e.g. image_url → cloud-metadata / an internal service).
//      ponytail: surface-not-fetch is the security ceiling; host-side download of
//      unfurl URLs would need an egress allowlist, not added speculatively.

import { renderSlackBlocks } from '../slack-rich-text.js';

interface SlackShareAttachment {
  text?: string;
  fallback?: string;
  /** ENG-7434: a directly pasted table arrives as an attachment whose own
   *  `blocks` carry the `table` block, with `fallback: "[no preview available]"`. */
  blocks?: unknown;
  files?: unknown[];
  message_blocks?: Array<{ message?: { blocks?: unknown } } | null>;
  title?: string;
  title_link?: string;
  from_url?: string;
  image_url?: string;
  thumb_url?: string;
}

export interface ForwardedContent {
  /** Text recovered from share attachments (body + link/image references), blank if none. */
  text: string;
  /** Uploaded file objects nested inside attachments (forwarded images/files). */
  files: unknown[];
}

function asTrimmed(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

export function extractForwardedContent(attachments: unknown): ForwardedContent {
  if (!Array.isArray(attachments)) return { text: '', files: [] };
  const parts: string[] = [];
  const files: unknown[] = [];
  for (const raw of attachments) {
    if (!raw || typeof raw !== 'object') continue;
    const att = raw as SlackShareAttachment;

    // Body: the forwarded message's text, fallback, or rendered blocks.
    // ENG-7430/ENG-7434: att.text / att.fallback omit table rows (the same gap
    // evt.text has, ENG-7424), so blocks are rendered even when text is present.
    // Sources: the attachment's own `blocks` (a directly pasted table lands
    // there with fallback "[no preview available]") plus forwarded
    // message_blocks. The rendered body wins whenever it recovers a table, and
    // beats a fallback-only body outright (a fallback is a degraded
    // representation by definition); real att.text keeps its short-circuit for
    // non-table content.
    const textBody = asTrimmed(att.text);
    let body = textBody || asTrimmed(att.fallback);
    const blockSources: unknown[] = [];
    if (Array.isArray(att.blocks)) blockSources.push(att.blocks);
    if (Array.isArray(att.message_blocks)) {
      for (const mb of att.message_blocks) blockSources.push(mb?.message?.blocks);
    }
    for (const source of blockSources) {
      const rendered = renderSlackBlocks(source);
      const renderedText = rendered.text.trim();
      if (renderedText && (!textBody || rendered.hadTable)) {
        body = renderedText;
        break;
      }
    }

    // Link/image unfurl metadata — surfaced as text, never fetched here.
    const title = asTrimmed(att.title);
    const link = asTrimmed(att.title_link) || asTrimmed(att.from_url);
    const imageUrl = asTrimmed(att.image_url) || asTrimmed(att.thumb_url);

    const lines: string[] = [];
    if (body) lines.push(body);
    if (title && title !== body) lines.push(title);
    if (link) lines.push(link);
    if (imageUrl) lines.push(`[image] ${imageUrl}`);
    if (lines.length) parts.push(lines.join('\n'));

    if (Array.isArray(att.files)) files.push(...att.files);
  }
  return { text: parts.join('\n\n'), files };
}

/** Cheap presence check for the inbound filter — does any attachment carry content? */
export function hasForwardedContent(attachments: unknown): boolean {
  const { text, files } = extractForwardedContent(attachments);
  return text.length > 0 || files.length > 0;
}
